// ============================================
// Device status protocol (firmware v0.5.38 `ui on` / `status`)
// ============================================
//
// v0.5.38 replaces banner-scraping with a single machine-readable object:
//   {"t":"status","state":"IDLE","sub":"none","ui":false,"mac":"D9:8E:C5:AA:19:84",
//    "fw":"v0.5.38","hw":"nRF52840-AAF0","impact_g":7,"event_g":2,"profile":"FULL",
//    "phy":"Coded","broadcast":"ALL","records":0,"bytes_used":0,"bytes_total":8388608,
//    "records_left":220,"session_id":0,"store_full":false,"batt_mv":4012,"batt_pct":92,
//    "charge":"charging","usb":true,"uptime_ms":1234,"time_set":false}
//
// Parse a status line with `parseFactoryLine` (utils/factoryTest.js) — it returns
// any `{…}` line whose `t` is a string, so it handles status / ack / nack too.

// Substring that uniquely identifies a status line, for use as a command
// start-marker and a cheap scan hint.
import { t } from "i18n";

export const STATUS_MARKER = '"t":"status"';

const normMac = (v) =>
  String(v || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

/**
 * Map a parsed `{"t":"status"}` object to the modal's `devCfg` shape. Only the
 * fields actually present on the object are copied, so a partial/older status
 * doesn't clobber existing values when merged (`{...prev, ...statusToDevCfg}`).
 */
export function statusToDevCfg(status) {
  if (!status || typeof status !== "object") return {};
  const out = {};
  const put = (key, val) => {
    if (val !== undefined) out[key] = val;
  };

  put("fw", status.fw);
  put("hw", status.hw);
  if (status.mac !== undefined) out.mac = normMac(status.mac);
  put("state", status.state);
  put("sub", status.sub);
  put("ui", status.ui);
  put("impactG", status.impact_g);
  put("eventG", status.event_g);
  put("profile", status.profile);
  put("phy", status.phy);
  put("broadcast", status.broadcast);
  // v0.5.83+: live session-cast transmission. Absent on older firmware —
  // callers feature-detect with `castTx !== undefined`.
  put("castTx", status.cast_tx);
  put("battMv", status.batt_mv);
  put("battPct", status.batt_pct);
  put("charge", status.charge);
  put("usb", status.usb);
  put("records", status.records);
  put("events", status.events);
  put("bytesUsed", status.bytes_used);
  put("bytesTotal", status.bytes_total);
  put("recordsLeft", status.records_left);
  put("storeFull", status.store_full);
  put("sessionId", status.session_id);
  put("uptimeMs", status.uptime_ms);
  put("timeSet", status.time_set);
  put("calStored", status.cal_stored);
  put("calSavedEpochS", status.cal_saved_epoch_s);

  // Side effect, on purpose: every status passes through here, so this is
  // where the per-MAC "last trustworthy %" is kept up to date.
  noteBatteryStatus(status);

  return out;
}

/**
 * Map a live device state/sub to the LED legend slug it corresponds to, so the
 * legend panel can highlight the active entry. Returns null when there's no
 * single stable mapping (transient LEDs like impact/charging aren't derivable
 * from state alone).
 */
export function ledStateSlug(state, sub) {
  switch (state) {
    case "IDLE":
      return "idle";
    case "SESSION":
      return sub === "arming" ? "session-arming" : "session-recording";
    case "DFU_ADV":
      return "dfu";
    default:
      return null;
  }
}

// ============================================
// Live state tracking (firmware v0.5.81 `state` / `substate` events)
// ============================================

/** First firmware that pushes {"t":"state"} events and accepts `session`. */
export const LIVE_STATE_FW_MIN = [0, 5, 81];

/**
 * True when a firmware string like "v0.5.81" / "v0.5.81-demo" is at least
 * `min` ([major, minor, patch]). False for null/garbage — callers use this to
 * hide capabilities rather than guess.
 */
export function fwAtLeast(fw, min) {
  const m = /v?(\d+)\.(\d+)\.(\d+)/.exec(String(fw || ""));
  if (!m) return false;
  const ver = [Number(m[1]), Number(m[2]), Number(m[3])];
  for (let i = 0; i < 3; i++) {
    if (ver[i] !== min[i]) return ver[i] > min[i];
  }
  return true;
}

/**
 * Fold one parsed NDJSON event into a devCfg patch. Pure: returns a new patch
 * (or the input unchanged for irrelevant events). Callers fold events in
 * arrival order so the last writer wins — a status followed by a newer state
 * event resolves to the state event.
 *
 * Understood events:
 *   status              → full statusToDevCfg merge
 *   state (v0.5.81+)    → {state, sub:"none"} (sub resets on every transition)
 *   substate (v0.5.81+) → {sub} (+state when carried)
 *   ack/nack with state → {state} — the pre-0.5.81 degraded path; internal
 *                         transitions (session auto-end, DFU timeout) emit
 *                         unsolicited acks that carry the new state
 *   session ev          → arming/recording/sched_* mapped to SESSION subs,
 *                         for old-firmware responsiveness
 */
export function foldLiveEvent(patch, ev) {
  if (!ev || typeof ev !== "object") return patch;
  switch (ev.t) {
    case "status":
      return { ...patch, ...statusToDevCfg(ev) };
    case "state":
      if (typeof ev.to !== "string") return patch;
      return { ...patch, state: ev.to, sub: "none" };
    case "substate":
      if (typeof ev.sub !== "string") return patch;
      return {
        ...patch,
        sub: ev.sub,
        ...(typeof ev.state === "string" ? { state: ev.state } : {}),
      };
    case "ack":
    case "nack":
      if (typeof ev.state !== "string") return patch;
      return { ...patch, state: ev.state };
    case "session":
      switch (ev.ev) {
        case "arming":
          return { ...patch, state: "SESSION", sub: "arming" };
        case "recording":
        case "sched_reached":
          return { ...patch, state: "SESSION", sub: "recording" };
        case "sched_wait":
          return { ...patch, state: "SESSION", sub: "sched_wait" };
        default:
          return patch;
      }
    default:
      return patch;
  }
}

// ============================================
// Battery level while charging
// ============================================
//
// The wearable has no fuel gauge: `batt_pct` is a plain voltage → state-of-
// charge lookup. On the charger the measured voltage is inflated (charge
// current through the cell's internal resistance), so the live percentage
// reads optimistically high — a resting ~45% cell can report ~75% while
// charging. The number is only trustworthy when `charge` is "discharging"
// (or "complete", where the charger itself says it's full).
//
// The dashboard therefore renders the battery *level* off `charge`, never off
// the live `batt_pct` alone, and keeps the last trustworthy reading per MAC
// for the session so a unit that is plugged in still shows a sensible
// pre-charge estimate rather than the inflated live value.

/** Last trustworthy state-of-charge per normalised MAC, for this session. */
const lastGoodPct = new Map();

/** Test/debug hook: forget every cached level. */
export function resetBatteryCache() {
  lastGoodPct.clear();
}

/**
 * Remember a trustworthy level from a status object (or a devCfg patch:
 * both `batt_pct`/`charge`/`mac` and `battPct`/`charge`/`mac` are accepted).
 * Only "discharging" readings and "complete" (→ 100) are recorded.
 */
export function noteBatteryStatus(s) {
  if (!s || typeof s !== "object") return;
  const mac = normMac(s.mac);
  if (!mac) return;
  const pct = s.battPct ?? s.batt_pct;
  if (s.charge === "discharging" && typeof pct === "number") {
    lastGoodPct.set(mac, pct);
  } else if (s.charge === "complete") {
    lastGoodPct.set(mac, 100);
  }
}

/**
 * What to show for the battery, given a devCfg-shaped object
 * ({ mac, charge, battPct, battMv }). Pure apart from reading the cache.
 *
 * Returns:
 *   state    "discharging" | "charging" | "complete" | "unknown"
 *   level    trustworthy % to display as the level, or null when there is
 *            none (charging with nothing cached yet — don't invent one)
 *   estimate true when `level` is a cached pre-charge reading, not live
 *   livePct  the raw live batt_pct (for tooltips/debug — inflated while charging)
 *   mv       raw millivolts, if present
 *   label    short text: "72%", "Full", "Charging…", "~45% · charging"
 *   hint     tooltip text with the raw reading and, while charging, why the
 *            live % isn't shown
 */
export function batteryDisplay(cfg) {
  const c = cfg || {};
  const livePct = typeof c.battPct === "number" ? c.battPct : null;
  const mv = typeof c.battMv === "number" ? c.battMv : null;
  const mvStr = mv != null ? `${mv} mV` : null;
  const cached = lastGoodPct.get(normMac(c.mac));

  if (c.charge === "complete") {
    return {
      state: "complete",
      level: 100,
      estimate: false,
      livePct,
      mv,
      label: t("utils.wearableStatus.battery.full"),
      hint: [mvStr, t("utils.wearableStatus.battery.chargerComplete")]
        .filter(Boolean)
        .join(" · "),
    };
  }
  if (c.charge === "charging") {
    const level = typeof cached === "number" ? cached : null;
    const why =
      level != null
        ? t("utils.wearableStatus.battery.inflatedWithLevel", { level })
        : t("utils.wearableStatus.battery.inflatedNoLevel");
    return {
      state: "charging",
      level,
      estimate: level != null,
      livePct,
      mv,
      label:
        level != null
          ? t("utils.wearableStatus.battery.chargingEstimate", { level })
          : t("utils.wearableStatus.battery.charging"),
      hint: [
        livePct != null
          ? t("utils.wearableStatus.battery.liveReading", { pct: livePct })
          : null,
        mvStr,
        why,
      ]
        .filter(Boolean)
        .join(" · "),
    };
  }
  if (c.charge === "discharging" || (c.charge == null && livePct != null)) {
    return {
      state: livePct != null ? "discharging" : "unknown",
      level: livePct,
      estimate: false,
      livePct,
      mv,
      label: livePct != null ? `${livePct}%` : "—",
      hint: mvStr || "",
    };
  }
  return {
    state: "unknown",
    level: null,
    estimate: false,
    livePct,
    mv,
    label: "—",
    hint: mvStr || "",
  };
}

/**
 * Battery colour from a trustworthy level only. Null (no level) → neutral, so
 * a nearly-dead unit on the charger never looks healthy and a healthy one
 * never looks critical.
 */
export function batteryTone(level) {
  if (level == null) return "#6B7280";
  if (level <= 15) return "#DC2626";
  if (level <= 35) return "#D97706";
  return "#15803D";
}
